import { generateClient } from 'aws-amplify/api'
import { getMySoftphoneRegistration as getMySoftphoneRegistrationQuery } from '@/graphql/queries'
import {
  setAgentSoftphoneExtension as setAgentSoftphoneExtensionMutation,
  initiateSoftphoneCall as initiateSoftphoneCallMutation,
  initiateNextSoftphoneAutoDialCall as initiateNextSoftphoneAutoDialCallMutation,
} from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage } from '@/mixins/Extras/ApiResponse'

// Lazy client — module import runs before Amplify.configure() in main.js.
let client = null
function getClient() {
  if (!client) client = generateClient()
  return client
}

async function runSoftphoneField(document, field, input) {
  try {
    const variables = input == null ? undefined : { input }
    const { data, errors } = await getClient().graphql({
      query: document,
      ...(variables ? { variables } : {}),
    })

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    return parseAwsJson(data?.[field])
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Request failed'),
    }
  }
}

/**
 * SIP registration for the calling user.
 * data: { extension, sip_password, websocket_uri }.
 * Fails with a clear message if no extension has been provisioned yet —
 * call setAgentSoftphoneExtension({ user_id }) then retry.
 */
export function getMySoftphoneRegistration() {
  return runSoftphoneField(
    getMySoftphoneRegistrationQuery,
    'getMySoftphoneRegistration',
  )
}

/**
 * Auto-provisions a SIP extension for a user (once).
 * Caller must be OWNER/ADMIN in the same org, or the target themself.
 * Returns { extension } — password is only available via getMySoftphoneRegistration.
 */
export function setAgentSoftphoneExtension(input = {}) {
  const userId = String(input.user_id ?? input.userId ?? '').trim()
  if (!userId) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'user_id is required.',
    })
  }

  return runSoftphoneField(
    setAgentSoftphoneExtensionMutation,
    'setAgentSoftphoneExtension',
    { user_id: userId },
  )
}

/**
 * Server-triggered outbound call from inside a task (AMI origination):
 * rings the agent's own softphone first, then bridges to the lead once
 * answered. data: { call_uuid }.
 *
 * Input (schema): task_id + request_id — both required. The server resolves
 * the destination number from the lead; the client does not send to_number.
 */
export function initiateSoftphoneCall(input = {}) {
  const taskId = String(input.task_id ?? input.taskId ?? '').trim()
  const requestId = String(input.request_id ?? input.requestId ?? '').trim()

  if (!taskId || !requestId) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'task_id and request_id are required.',
    })
  }

  return runSoftphoneField(
    initiateSoftphoneCallMutation,
    'initiateSoftphoneCall',
    {
      task_id: taskId,
      request_id: requestId,
    },
  )
}

function normalizeAutoDialCallEntry(row = {}, index = 0) {
  if (!row || typeof row !== 'object') return null
  const callUuid = String(row.call_uuid ?? row.callUuid ?? '').trim()
  const requestId = String(row.request_id ?? row.requestId ?? '').trim()
  if (!callUuid && !requestId) return null
  return {
    id: callUuid || requestId || `call-${index}`,
    callUuid,
    requestId,
    toNumber: String(row.to_number ?? row.toNumber ?? row.phone ?? '').trim(),
    contactName: String(
      row.contact_name ?? row.contactName ?? row.caller_name ?? row.name ?? '',
    ).trim(),
    raw: row,
  }
}

/**
 * Normalize initiateNextSoftphoneAutoDialCall payload.
 * Supports legacy single-call shape and the new dial_ratio batch:
 * { dial_batch_id, calls: [{ call_uuid, request_id, ... }] }.
 */
export function normalizeSoftphoneAutoDialResponse(response) {
  const root =
    response?.data && typeof response.data === 'object' ? response.data : response
  if (!root || typeof root !== 'object') {
    return {
      dialBatchId: '',
      calls: [],
      callUuid: '',
      requestId: '',
    }
  }

  const dialBatchId = String(
    root.dial_batch_id ?? root.dialBatchId ?? '',
  ).trim()

  let calls = []
  if (Array.isArray(root.calls)) {
    calls = root.calls
      .map((row, index) => normalizeAutoDialCallEntry(row, index))
      .filter(Boolean)
  } else {
    // Legacy single-call response
    const single = normalizeAutoDialCallEntry(root, 0)
    if (single) calls = [single]
  }

  const primary = calls[0] || null
  return {
    dialBatchId,
    calls,
    callUuid: primary?.callUuid || '',
    requestId: primary?.requestId || '',
  }
}

/**
 * Softphone predictive auto-dial "ready" signal for a HUMAN_AGENT campaign.
 * Returns a batch of calls sized by the task's dial_ratio (keep at 1 for now).
 *
 * Normalized data: { dialBatchId, calls[{ callUuid, requestId, ... }], callUuid, requestId }.
 */
export async function initiateNextSoftphoneAutoDialCall(input = {}) {
  const taskId = String(input.task_id ?? input.taskId ?? '').trim()
  if (!taskId) {
    return {
      status: 'ERROR',
      message: 'task_id is required.',
      data: { dialBatchId: '', calls: [], callUuid: '', requestId: '' },
    }
  }

  const response = await runSoftphoneField(
    initiateNextSoftphoneAutoDialCallMutation,
    'initiateNextSoftphoneAutoDialCall',
    { task_id: taskId },
  )

  const normalized = normalizeSoftphoneAutoDialResponse(response)
  if (response?.status === 'ERROR') {
    return {
      ...response,
      data: normalized,
    }
  }

  return {
    ...response,
    data: normalized,
    // Convenience mirrors for callers that only need the first call.
    call_uuid: normalized.callUuid,
    request_id: normalized.requestId,
    dial_batch_id: normalized.dialBatchId,
    calls: normalized.calls,
  }
}
